import React, { useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  ArrowDownRight,
  ArrowRight,
  CalendarDays,
  Check,
  ChevronDown,
  CircleDollarSign,
  HeartHandshake,
  LayoutDashboard,
  LockKeyhole,
  Menu,
  MessageCircleMore,
  Play,
  ShieldCheck,
  Sparkles,
  UsersRound,
  X,
} from "lucide-react";
import "./styles.css";

const modules = [
  { name: "Menschen", detail: "Kontakte, Gruppen, Rollen", icon: UsersRound },
  { name: "Planung", detail: "Anlässe, Teams, Räume", icon: CalendarDays },
  { name: "Kommunikation", detail: "App, Newsletter, Nachrichten", icon: MessageCircleMore },
  { name: "Finanzen", detail: "Spenden, Budgets, Auswertungen", icon: CircleDollarSign },
  { name: "Website", detail: "Inhalte, Formulare, Publikation", icon: LayoutDashboard },
];

const heroFlows = [
  {
    name: "Menschen", icon: UsersRound, topbar: "Menschen im Mittelpunkt",
    eyebrow: "Neue Person", title: "Sarah Keller", meta: "Team Willkommen · aktiv",
    roster: "Profil mit 3 Bereichen verbunden", tasksLabel: "Nächste Schritte",
    tasks: ["Profil bestätigt", "Team zuordnen", "Willkommensmail senden"],
    signalLabel: "Verbindung", signalTitle: "Bereit für den nächsten Schritt",
    status: "Profil, Team und Kommunikation sind synchronisiert",
  },
  {
    name: "Planung", icon: CalendarDays, topbar: "Planung im Überblick",
    eyebrow: "Nächster Anlass", title: "Sonntagsfeier", meta: "09:30 · Hauptsaal",
    roster: "18 Personen eingeplant", tasksLabel: "Deine Aufgaben",
    tasks: ["Teamplan freigegeben", "Raum bestätigen", "Ablauf teilen"],
    signalLabel: "Diese Woche", signalTitle: "Alles im Fluss",
    status: "Anlass, Teams und Räume sind synchronisiert",
  },
  {
    name: "Kommunikation", icon: MessageCircleMore, topbar: "Kommunikation, die ankommt",
    eyebrow: "Newsletter", title: "Wochenimpuls", meta: "124 Empfänger · Entwurf",
    roster: "3 Zielgruppen automatisch gewählt", tasksLabel: "Vor dem Versand",
    tasks: ["Inhalt geprüft", "Vorschau öffnen", "Versand einplanen"],
    signalLabel: "Reichweite", signalTitle: "Die richtigen Menschen erreichen",
    status: "Kontakte, Gruppen und Newsletter sind synchronisiert",
  },
  {
    name: "Finanzen", icon: CircleDollarSign, topbar: "Finanzen mit Überblick",
    eyebrow: "Monatsübersicht", title: "August im Plan", meta: "12 Buchungen offen",
    roster: "Spenden und Budget aktuell", tasksLabel: "Zu prüfen",
    tasks: ["Import abgeschlossen", "Belege zuordnen", "Monat freigeben"],
    signalLabel: "Verantwortung", signalTitle: "Transparent entscheiden",
    status: "Spenden, Buchhaltung und Reporting sind synchronisiert",
  },
];

function useReveal() {
  useEffect(() => {
    const nodes = [...document.querySelectorAll("[data-reveal]")];
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      nodes.forEach((node) => node.classList.add("is-visible"));
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => entry.isIntersecting && entry.target.classList.add("is-visible")),
      { threshold: 0.12 },
    );
    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, []);
}

function Logo({ inverse = false, refined = false }) {
  return (
    <a className={`logo ${inverse ? "logo--inverse" : ""} ${refined ? "logo--refined" : ""}`} href="#top" aria-label="ChurchPoint Startseite">
      {refined ? (
        <svg viewBox="0 0 72 72" role="img" aria-hidden="true">
          <path className="logo-refined-path" d="M16 63V31.5C16 17.4 26.8 7 40.5 7S65 17.4 65 31.5 54.2 56 40.5 56H34" />
          <circle className="logo-refined-point" cx="40.5" cy="31.5" r="7.6" />
        </svg>
      ) : (
        <svg viewBox="0 0 42 42" role="img" aria-hidden="true">
          <path d="M21 2.8C11.3 2.8 4 9.9 4 19.2c0 9.9 8 17.7 17 20 9-2.3 17-10.1 17-20C38 9.9 30.7 2.8 21 2.8Z" fill="currentColor" />
          <path d="M21 9.2c-5.4 0-9.5 4.1-9.5 9.3 0 5.5 4.5 9.9 9.5 11.2 5-1.3 9.5-5.7 9.5-11.2 0-5.2-4.1-9.3-9.5-9.3Z" fill="var(--logo-cut, #f8f6ef)" />
          <circle cx="21" cy="18.8" r="4.5" fill="#38a84e" />
        </svg>
      )}
      <span>Church<span>Point</span></span>
    </a>
  );
}

function VariantSwitch({ variant }) {
  const local = ["localhost", "127.0.0.1"].includes(window.location.hostname);
  const href = variant === "a"
    ? (local ? "/?variant=b" : "https://b.churchpoint.net")
    : (local ? "/?variant=a" : "https://a.churchpoint.net");
  return (
    <a className="variant-switch" href={href} aria-label={`Zu Entwurf ${variant === "a" ? "B" : "A"} wechseln`}>
      <span>Entwurf {variant.toUpperCase()}</span>
      <strong>{variant === "a" ? "B" : "A"} ansehen</strong>
      <ArrowRight size={15} aria-hidden="true" />
    </a>
  );
}

function Navigation({ dark = false, refined = false }) {
  const [open, setOpen] = useState(false);
  return (
    <header className={`nav-shell ${dark ? "nav-shell--dark" : ""}`}>
      <Logo inverse={dark} refined={refined} />
      <button className="menu-button" type="button" aria-label={open ? "Menü schließen" : "Menü öffnen"} aria-expanded={open} onClick={() => setOpen(!open)}>
        {open ? <X /> : <Menu />}
      </button>
      <nav className={open ? "is-open" : ""} aria-label="Hauptnavigation">
        <a href="#produkt" onClick={() => setOpen(false)}>Produkt</a>
        <a href="#module" onClick={() => setOpen(false)}>Module <ChevronDown size={13} aria-hidden="true" /></a>
        <a href="#sicherheit" onClick={() => setOpen(false)}>Sicherheit</a>
        <a href="#menschen" onClick={() => setOpen(false)}>Für wen</a>
      </nav>
      <div className="nav-actions">
        <a className="text-link" href="#produkt">Demo ansehen</a>
        <a className="button button--small" href="#kontakt">Kostenlos starten <ArrowRight size={16} /></a>
      </div>
    </header>
  );
}

function MiniAvatar({ n }) {
  return <span className={`mini-avatar mini-avatar--${n}`} aria-hidden="true" />;
}

function ProductSurface({ dark = false, flow = null }) {
  const content = flow || {
    topbar: "Heute in deiner Gemeinde",
    eyebrow: "Nächster Anlass",
    title: "Sonntagsfeier",
    meta: "09:30 · Hauptsaal",
    roster: "18 Personen eingeplant",
    tasksLabel: "Deine Aufgaben",
    tasks: ["Teamplan freigeben", "Newsletter vorbereiten", "Raum bestätigen"],
    signalLabel: "Diese Woche",
    signalTitle: "Alles im Fluss",
    status: "4 Bereiche sind gerade miteinander synchronisiert",
  };
  return (
    <div className={`product-surface ${dark ? "product-surface--dark" : ""}`} data-flow={flow?.name?.toLowerCase()} aria-label="Interaktive Vorschau des ChurchPoint Dashboards">
      <div className="product-topbar">
        <span className="product-dot"><span /></span>
        <span>{content.topbar}</span>
        <div className="product-avatars"><MiniAvatar n="1" /><MiniAvatar n="2" /><MiniAvatar n="3" /></div>
      </div>
      <div className="product-grid" key={content.title}>
        <article className="product-panel product-panel--agenda">
          <span className="eyebrow">{content.eyebrow}</span>
          <strong>{content.title}</strong>
          <p>{content.meta}</p>
          <div className="roster">
            <MiniAvatar n="1" /><MiniAvatar n="2" /><MiniAvatar n="3" />
            <span>{content.roster}</span>
          </div>
        </article>
        <article className="product-panel product-panel--tasks">
          <span className="eyebrow">{content.tasksLabel}</span>
          {content.tasks.map((task, index) => (
            <label key={task}><span className={`fake-check ${index === 0 ? "is-checked" : ""}`}>{index === 0 && <Check size={11} />}</span> {task}</label>
          ))}
        </article>
        <article className="product-panel product-panel--signal">
          <span className="eyebrow">{content.signalLabel}</span>
          <strong>{content.signalTitle}</strong>
          <svg viewBox="0 0 260 58" aria-hidden="true">
            <path d="M2 48C31 47 29 30 56 33s29 15 53 3 29-25 54-14 31 22 50 7 27-16 45-19" fill="none" stroke="currentColor" strokeWidth="3" />
          </svg>
        </article>
      </div>
      <div className="product-status"><span /> {content.status}</div>
    </div>
  );
}

function ProofLine({ dark = false }) {
  return (
    <div className={`proof-line ${dark ? "proof-line--dark" : ""}`}>
      <span><ShieldCheck size={18} /> In der Schweiz gehostet</span>
      <span><LockKeyhole size={18} /> Datenschutz im System gedacht</span>
      <span><HeartHandshake size={18} /> Für Gemeinden entwickelt</span>
    </div>
  );
}

function VariantA() {
  useReveal();
  return (
    <main id="top" className="site site-a">
      <Navigation />
      <section className="a-hero">
        <div className="hero-kicker"><span>01</span> Die Plattform für verbundene Gemeindearbeit</div>
        <div className="a-hero-copy" data-reveal>
          <h1>Alles an einem <em>Punkt.</em><br />Mehr Zeit für Menschen.</h1>
          <p>ChurchPoint verbindet Personen, Planung, Kommunikation und Finanzen in einem ruhigen System – damit Gemeindearbeit wieder dort wirkt, wo sie zählt.</p>
          <div className="hero-actions">
            <a className="button" href="#kontakt">Kostenlos starten <ArrowDownRight size={19} /></a>
            <a className="play-link" href="#produkt"><span><Play size={15} fill="currentColor" /></span> Produkt erleben</a>
          </div>
        </div>
        <div className="a-product-stage" data-reveal>
          <div className="stage-index">CHP / 01</div>
          <div className="connector connector--one" aria-hidden="true" />
          <div className="connector connector--two" aria-hidden="true" />
          <span className="stage-tag stage-tag--people"><UsersRound size={16} /> 1 Person ergänzt</span>
          <span className="stage-tag stage-tag--message"><MessageCircleMore size={16} /> Nachricht bereit</span>
          <ProductSurface />
        </div>
      </section>

      <ProofLine />

      <section id="produkt" className="a-story section-pad">
        <div className="section-number">02 / VERBINDUNG</div>
        <div className="a-story-intro" data-reveal>
          <h2>Ein Arbeitsschritt.<br /><span>Überall aktuell.</span></h2>
          <p>ChurchPoint denkt nicht in isolierten Modulen, sondern in Beziehungen. Eine Änderung fließt dorthin, wo sie gebraucht wird.</p>
        </div>
        <div className="flow-line" data-reveal>
          <div className="flow-item"><span>01</span><strong>Person eintragen</strong><p>Kontaktdaten und Rolle sind zentral gepflegt.</p></div>
          <div className="flow-arrow"><ArrowRight /></div>
          <div className="flow-item"><span>02</span><strong>Team planen</strong><p>Verfügbarkeit und Anlass greifen ineinander.</p></div>
          <div className="flow-arrow"><ArrowRight /></div>
          <div className="flow-item"><span>03</span><strong>Alle erreichen</strong><p>Informationen landen automatisch am richtigen Ort.</p></div>
        </div>
      </section>

      <section id="module" className="a-modules section-pad">
        <div className="module-heading" data-reveal>
          <div>
            <span className="section-number">03 / PLATTFORM</span>
            <h2>Eine Plattform.<br />Fünf klare Bereiche.</h2>
          </div>
          <p>Starte mit dem, was deine Gemeinde heute braucht. Ergänze später, ohne Daten oder Abläufe neu aufzubauen.</p>
        </div>
        <div className="editorial-modules">
          {modules.map(({ name, detail, icon: Icon }, index) => (
            <article key={name} data-reveal>
              <span className="module-no">0{index + 1}</span>
              <Icon aria-hidden="true" />
              <h3>{name}</h3>
              <p>{detail}</p>
              <ArrowDownRight className="module-arrow" aria-hidden="true" />
            </article>
          ))}
        </div>
      </section>

      <section id="menschen" className="human-section section-pad">
        <div className="human-photo" data-reveal>
          <img src="/assets/community-team.png" alt="Ein Gemeindeteam arbeitet gemeinsam an Planung und Organisation" loading="lazy" />
          <span className="photo-caption">Menschen führen. Teams befähigen. Gemeinschaft gestalten.</span>
        </div>
        <div className="human-copy" data-reveal>
          <span className="section-number">04 / MENSCHEN</span>
          <blockquote>„Technik soll im Hintergrund ruhig funktionieren – damit vorne echte Begegnung möglich wird.“</blockquote>
          <p>Für Pfarrpersonen, Administration, Ressortleitungen und Freiwillige. Jede Rolle sieht, was sie braucht – nicht mehr und nicht weniger.</p>
          <a className="line-link" href="#kontakt">ChurchPoint kennenlernen <ArrowRight size={17} /></a>
        </div>
      </section>

      <section id="sicherheit" className="security-section section-pad">
        <div className="security-mark"><ShieldCheck aria-hidden="true" /></div>
        <div data-reveal>
          <span className="section-number">05 / VERTRAUEN</span>
          <h2>Schweizer Infrastruktur.<br />Klare Verantwortung.</h2>
        </div>
        <div className="security-copy" data-reveal>
          <p>Datenschutz, Berechtigungen und nachvollziehbare Abläufe sind kein Zusatz. Sie gehören zum Fundament.</p>
          <ul><li><Check /> Rollenbasierte Zugriffe</li><li><Check /> Sichere Schweizer Datenhaltung</li><li><Check /> Persönlicher Support</li></ul>
        </div>
      </section>

      <FinalCta variant="a" />
      <Footer />
      <VariantSwitch variant="a" />
    </main>
  );
}

function Constellation() {
  const [active, setActive] = useState(1);
  const [reducedMotion, setReducedMotion] = useState(false);
  const lastInteraction = useRef(0);
  const constellationRef = useRef(null);

  useEffect(() => {
    const preference = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updatePreference = () => setReducedMotion(preference.matches);
    updatePreference();
    preference.addEventListener?.("change", updatePreference);
    return () => preference.removeEventListener?.("change", updatePreference);
  }, []);

  useEffect(() => {
    if (reducedMotion) return undefined;
    const timer = window.setInterval(() => {
      if (Date.now() - lastInteraction.current > 6500) setActive((current) => (current + 1) % heroFlows.length);
    }, 3900);
    return () => window.clearInterval(timer);
  }, [reducedMotion]);

  const selectFlow = (index) => {
    lastInteraction.current = Date.now();
    setActive(index);
  };

  const handlePointerMove = (event) => {
    if (reducedMotion || event.pointerType === "touch") return;
    const rect = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width - 0.5) * 2;
    const y = ((event.clientY - rect.top) / rect.height - 0.5) * 2;
    event.currentTarget.style.setProperty("--point-x", `${x * 9}px`);
    event.currentTarget.style.setProperty("--point-y", `${y * 7}px`);
  };

  const resetPointer = () => {
    constellationRef.current?.style.setProperty("--point-x", "0px");
    constellationRef.current?.style.setProperty("--point-y", "0px");
  };

  return (
    <div className="constellation" ref={constellationRef} onPointerMove={handlePointerMove} onPointerLeave={resetPointer} aria-label="Interaktiver ChurchPoint Produkt-Prototyp">
      <div className="prototype-label"><span /> Live-Prototyp <i>Bereich wählen</i></div>
      <div className="orbit orbit--one" aria-hidden="true" />
      <div className="orbit orbit--two" aria-hidden="true" />
      <svg className="point-flow-web" viewBox="0 0 650 650" aria-hidden="true">
        {[
          "M 30 180 C 160 180, 190 280, 325 325",
          "M 620 150 C 490 165, 455 245, 325 325",
          "M 55 540 C 175 500, 205 405, 325 325",
          "M 620 490 C 500 475, 455 390, 325 325",
        ].map((path, index) => <path key={path} className={active === index ? "is-active" : ""} d={path} />)}
        {!reducedMotion && [
          "M 30 180 C 160 180, 190 280, 325 325",
          "M 620 150 C 490 165, 455 245, 325 325",
          "M 55 540 C 175 500, 205 405, 325 325",
          "M 620 490 C 500 475, 455 390, 325 325",
        ].map((path, index) => (
          <circle className={active === index ? "flow-point is-active" : "flow-point"} r={active === index ? 4 : 2.5} key={`point-${index}`}>
            <animateMotion dur={`${3.1 + index * 0.35}s`} repeatCount="indefinite" path={path} />
          </circle>
        ))}
      </svg>
      <div className="point-cloud" aria-hidden="true">
        {Array.from({ length: 34 }, (_, index) => <i key={index} style={{ "--x": `${(index * 37) % 100}%`, "--y": `${(index * 61) % 100}%`, "--delay": `${(index % 8) * -0.24}s` }} />)}
      </div>
      <div className="core-product"><ProductSurface dark flow={heroFlows[active]} /></div>
      {heroFlows.map(({ name, icon: Icon }, index) => (
        <button type="button" className={`orbit-node orbit-node--${index + 1} ${active === index ? "is-active" : ""}`} aria-pressed={active === index} onClick={() => selectFlow(index)} onPointerEnter={() => selectFlow(index)} onFocus={() => selectFlow(index)} key={name}>
          <Icon size={17} aria-hidden="true" /><span>{name}</span>
        </button>
      ))}
      <div className="signal-pulse" aria-hidden="true" />
      <p className="prototype-hint" aria-live="polite"><strong>{heroFlows[active].name}</strong> · Beispieldaten im interaktiven Prototyp</p>
    </div>
  );
}

function VariantB() {
  useReveal();
  return (
    <main id="top" className="site site-b">
      <Navigation dark refined />
      <section className="b-hero">
        <div className="b-ambient" aria-hidden="true" />
        <div className="b-hero-copy" data-reveal>
          <span className="signal-label"><i /> ChurchPoint verbindet, was Gemeinde bewegt</span>
          <h1>Mehr <span>Gemeinde.</span><br />Weniger Verwaltung.</h1>
          <p>Ein digitales Betriebssystem für Menschen, Teams und alle Aufgaben dazwischen.</p>
          <div className="hero-actions">
            <a className="button button--glow" href="#kontakt">ChurchPoint testen <ArrowRight size={19} /></a>
            <a className="text-link text-link--light" href="#produkt">So funktioniert es</a>
          </div>
        </div>
        <div className="b-hero-visual" data-reveal><Constellation /></div>
        <div className="scroll-cue"><span>Scroll</span><i /></div>
      </section>

      <ProofLine dark />

      <section id="produkt" className="b-journey section-pad">
        <header data-reveal>
          <span className="signal-label"><i /> Ein System statt fünf Insellösungen</span>
          <h2>Von verstreuten Aufgaben<br />zu einem gemeinsamen Rhythmus.</h2>
        </header>
        <div className="journey-grid">
          <article data-reveal><span>01</span><h3>Erkennen</h3><p>Termine, Personen und offene Aufgaben werden in einem klaren Tagesbild sichtbar.</p></article>
          <article data-reveal><span>02</span><h3>Verbinden</h3><p>Änderungen fließen automatisch zwischen Planung, Teams und Kommunikation.</p></article>
          <article data-reveal><span>03</span><h3>Wirken</h3><p>Weniger Nachfragen. Mehr Verlässlichkeit. Mehr Energie für echte Gemeindearbeit.</p></article>
        </div>
      </section>

      <section id="module" className="b-module-stage section-pad">
        <div className="b-module-copy" data-reveal>
          <span className="signal-label"><i /> Modular und gemeinsam gedacht</span>
          <h2>Alles greift<br /><em>ineinander.</em></h2>
          <p>Jeder Bereich kann für sich starten. Seine volle Stärke entsteht im Zusammenspiel.</p>
        </div>
        <div className="b-module-list">
          {modules.map(({ name, detail, icon: Icon }, index) => (
            <article key={name} data-reveal>
              <span>0{index + 1}</span><Icon aria-hidden="true" /><h3>{name}</h3><p>{detail}</p><ArrowDownRight aria-hidden="true" />
            </article>
          ))}
        </div>
      </section>

      <section id="menschen" className="b-human section-pad">
        <div className="b-photo" data-reveal>
          <img src="/assets/community-team.png" alt="Ein Gemeindeteam arbeitet gemeinsam an einem Tisch" loading="lazy" />
          <div className="photo-wash" />
        </div>
        <div className="b-human-copy" data-reveal>
          <Sparkles aria-hidden="true" />
          <span className="signal-label"><i /> Für die Menschen hinter der Gemeinde</span>
          <h2>Software, die Raum schafft.</h2>
          <p>ChurchPoint übernimmt nicht die Beziehung. Es nimmt ihr die Reibung: mit klaren Abläufen, richtigen Informationen und weniger administrativem Lärm.</p>
        </div>
      </section>

      <section id="sicherheit" className="b-trust section-pad">
        <div className="trust-orb"><LockKeyhole aria-hidden="true" /></div>
        <div data-reveal>
          <span className="signal-label"><i /> Vertrauen ist Infrastruktur</span>
          <h2>Sicher in der Schweiz.<br />Einfach für dein Team.</h2>
        </div>
        <div className="trust-facts" data-reveal>
          <span>Rollenbasierte Zugriffe</span><span>Schweizer Datenhaltung</span><span>Nachvollziehbare Abläufe</span><span>Persönlicher Support</span>
        </div>
      </section>

      <FinalCta variant="b" />
      <Footer dark refined />
      <VariantSwitch variant="b" />
    </main>
  );
}

function FinalCta({ variant }) {
  return (
    <section id="kontakt" className={`final-cta final-cta--${variant}`}>
      <div>
        <span>{variant === "a" ? "06 / NÄCHSTER SCHRITT" : "BEREIT, WENN IHR ES SEID"}</span>
        <h2>Mehr Zeit für Menschen<br />beginnt an einem Punkt.</h2>
      </div>
      <div>
        <p>ChurchPoint unverbindlich kennenlernen und gemeinsam herausfinden, was deine Gemeinde wirklich braucht.</p>
        <a className="button" href="mailto:hallo@churchpoint.net?subject=ChurchPoint%20Demo">Demo anfragen <ArrowRight size={18} /></a>
      </div>
    </section>
  );
}

function Footer({ dark = false, refined = false }) {
  return (
    <footer className={dark ? "footer footer--dark" : "footer"}>
      <Logo inverse={dark} refined={refined} />
      <p>Ein Entwurf für ChurchPoint · Schweiz</p>
      <div><a href="#sicherheit">Datenschutz</a><a href="#kontakt">Kontakt</a></div>
    </footer>
  );
}

const queryVariant = new URLSearchParams(window.location.search).get("variant");
const hostVariant = window.location.hostname.toLowerCase().startsWith("b.") ? "b" : "a";
const variant = queryVariant === "b" ? "b" : queryVariant === "a" ? "a" : hostVariant;

document.documentElement.dataset.variant = variant;
createRoot(document.getElementById("root")).render(variant === "b" ? <VariantB /> : <VariantA />);
